import { Button } from 'components';
import { Snackbar } from 'components/Snackbar';
import { DashboardTitle } from 'components/Titles';
import { blinkPageTitle, playNotificationSound } from 'helpers/utils';
import { usePet } from 'hooks/usePet';
import { useUser } from 'hooks/useUser';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { AppointmentService } from 'services';
import {
  onMessageListener,
  requestNotificationPermission,
} from 'services/firebase.service';
import { io } from 'socket.io-client';
import useStayAwake from 'use-stay-awake';

import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { Grid, Paper, Typography } from '@mui/material';

import { Panel } from './Panel';
import { SocketTreatmentEvents } from 'types';
import { useInterval } from 'hooks/useInterval';
import { useTreatmentInQueue } from 'hooks/useTreatmentInQueue';

const REFRESH_INTERVAL = 1000 * 60 * 2; // two minutes to refresh queue if socket disconnect

export const Dashboard: FC = () => {
  const { user, getUser } = useUser();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { pet, setAppointmentOnHold } = usePet();
  const [quantity, setQuantity] = useState(0);
  const [ring, setRing] = useState<any>(null);
  const [petMessage, setPetMessage] = useState<string | null>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const device = useStayAwake();
  const socket = useMemo(() => {
    return io(process.env.REACT_APP_BASE_API_URL as string, { path: '/ws' });
  }, []);

  const getQueue = async () => {
    try {
      const response = await AppointmentService.inQueueLength();
      if (response.status === 200) {
        const { petsInQueue } = response.data;
        setQuantity(petsInQueue);
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);
      }
    }
  };

  const handleStartTreatment = async () => {
    try {
      const clear = () => { clearInterval(ring) }
      setTimeout(clear, 100);
      await setAppointmentOnHold();
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);
      }
    }
    if (pet) history.push(`${path}/appointment`);
  };

  const getAppointmentOnHold = useCallback(async () => {
    try {
      await AppointmentService.getAppointmentInfo();

      history.push(`${path}/appointment`);
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);
      }
    }
  }, [history, path]);

  const getTreatmentOnGoing = useCallback(async () => {
    try {
      const response = await AppointmentService.getTreatmentOnGoing();
      if (response.status === 200) {
        history.push(`${path}/video-call`);
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);
      }
    }
  }, [history, path]);

  useEffect(() => {
  
    getTreatmentOnGoing();
    getAppointmentOnHold();
    getUser();
    getQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (quantity) {
      setRing(setInterval(playNotificationSound, 1000));
      setPetMessage('Atenção! Tutor solicitando atendimento ao Pet.');
      setSnackbarOpen(true);
    }
  }, [quantity]);

  useEffect(() => {
    try {
      // Prevent pc from sleep
      device.preventSleeping();

      // Request notification permission to user
      requestNotificationPermission();

      onMessageListener();
    } catch (error) {}
  }, [device]);

  useEffect(() => {
    socket.connect();
    socket.on(SocketTreatmentEvents.CONNECT, () => {
      // Update queue list
      socket.on(
        SocketTreatmentEvents.TUTOR_TREATMENT_UPDATE_VET_QUEUE,
        (data: { new: boolean }) => {
          getQueue();

          // Only play sound and blink title if is a new tratment in queue
           if (data?.new) {
          //   const ring = setInterval(playNotificationSound, 1000);
          //   const clear = () => { clearInterval(ring) }
          //   setTimeout(clear, 9000);
          blinkPageTitle('Tutor solicitando atendimento ao Pet.');
          }
        }
      );
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force refresh on specific interval if socket has not receiving connections
  useInterval(() => {


    if (socket.disconnected) {
      socket.connect();
    }

    getQueue();
  }, REFRESH_INTERVAL);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DashboardTitle title="Painel de Atendimentos" />
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid item container xs={12} spacing={2}>
          <Panel
            title="Pets"
            quantity={user.petsTreated}
            icon={<PeopleAltIcon sx={{ fontSize: 48 }} color="inherit" />}
          />
          <Panel
            title="Atendimentos"
            quantity={user.totalTreatments}
            icon={
              <AssignmentTurnedInIcon sx={{ fontSize: 48 }} color="inherit" />
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper
          variant="outlined"
          sx={{ border: '2px solid #FBB45F', textAlign: 'center', p: 4 }}
        >
          <Typography fontSize={18} fontWeight={400}>
            Neste momento há
          </Typography>
          <Typography fontSize={130} fontWeight={700}>
            {quantity}
          </Typography>
          <Typography fontSize={18} fontWeight={400}>
            Tutores na lista de espera
          </Typography>
          <Button
            sx={{ color: '#FFFFFF', fontWeight: 700, px: 8, py: 1, mt: 2 }}
            onClick={handleStartTreatment}
            disabled={quantity > 0 ? false : true}
          >
            Realizar atendimento
          </Button>
        </Paper>
        <Snackbar
          text={petMessage}
          snackbarOpen={snackbarOpen}
          setSnackbarOpen={setSnackbarOpen}
          severityType="warning"
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        />
      </Grid>
    </Grid>
  );
};
